import { ButtonGroup, FormControl, MenuItem, Select, TextField } from '@material-ui/core';
import cx from 'classnames';
import { icon_add } from 'components/base/images';
import MuiButton from 'components/common/button/MuiButton';
import WhiteButton from 'components/common/button/WhiteButton';
import CustomDatePicker from 'components/common/input/CustomDatePicker';
import MuiWrapper from 'components/common/input/MuiWrapper';
import AsyncFetchSelect from 'components/common/select/AsyncFetchSelect';
import { T } from 'components/common/text';
import CustomSpan from 'components/common/text/CustomSpan';
import CustomText from 'components/common/text/CustomText';
import DOMPurify from 'dompurify';
import { validInputString } from 'lib/library';
import {
  ceramicMetalIconList,
  occlusalSurfaceIconList,
  restorationPonticIconList,
} from 'lib/teeth/teethDesignMapper';
import { IProjectPatients } from 'lib/types/patientTypes';
import moment from 'moment';
import React, { useMemo } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { color } from 'styles/utils';

interface CreateProjectInfoProps {
  language: string;
  isClinicUser: boolean;
  pageType: string;
  projectPartners: {
    accountType: number;
    isSelf: number;
    name: string;
    userGroupIdx: number;
  }[];
  hasMoreProjectPartners: boolean;
  employeeList: {
    employeeIdx: number;
    userName: string;
    employeeNum: number;
    postPosition: string;
  }[];
  caseId: string;
  informationControl: any;
  informationErrors: object | any;
  informationWatch: any;
  remakeTypeList: any[];
  remake: number[];
  isClientError: {
    value: boolean;
    onChange: (e: any) => void;
    setValue: (value: boolean) => void;
  };
  clientName: {
    value: string;
    onChange: (e: any) => void;
    setValue: (value: string) => void;
  };
  isPatientError: {
    value: boolean;
    onChange: (e: any) => void;
    setValue: (value: boolean) => void;
  };
  patientName: {
    value: string;
    onChange: (e: any) => void;
    setValue: (value: string) => void;
  };
  patientCode: {
    value: string;
    onChange: (e: any) => void;
    setValue: (value: string) => void;
  };
  projectPatients: IProjectPatients[];
  hasMoreProjectPatients: boolean;
  projectPatientsPending: boolean;
  restorationPontic: {
    value: number;
    onChange: (e: any) => void;
    setValue: (value: number) => void;
  };
  occlusalSurface: {
    value: number;
    onChange: (e: any) => void;
    setValue: (value: number) => void;
  };
  ceramicMetal: {
    value: number;
    onChange: (e: any) => void;
    setValue: (value: number) => void;
  };
  isExternalSelected: boolean | null;
  onFetchProjectPartners: () => void;
  onFetchProjectPatients: () => void;
  onSearchProjectPatients: (value: string) => void;
  onClickPartnerModal: () => void;
  onClickPatientModal: () => void;
  onClickRemakeModal: () => void;
  isProjectCreator: boolean;
}

const remakeDummy = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

function CreateProjectInfo({
  language,
  isClinicUser,
  pageType,
  projectPartners,
  hasMoreProjectPartners,
  employeeList,
  caseId,
  informationControl,
  informationErrors,
  informationWatch,
  remakeTypeList,
  remake,
  isClientError,
  clientName,
  isPatientError,
  patientName,
  patientCode,
  projectPatients,
  hasMoreProjectPatients,
  projectPatientsPending,
  restorationPontic,
  occlusalSurface,
  ceramicMetal,
  isExternalSelected,
  onFetchProjectPatients,
  onSearchProjectPatients,
  onFetchProjectPartners,
  onClickPartnerModal,
  onClickPatientModal,
  onClickRemakeModal,
  isProjectCreator,
}: CreateProjectInfoProps) {
  const projectTitleList = [
    {
      pageType: 'create',
      pageTitle: 'New Project',
      code: 'PROJECT_NEW_TITLE',
    },
    {
      pageType: 'edit',
      pageTitle: 'Edit Project',
      code: 'PROJECT_EDIT_TITLE',
    },
    {
      pageType: 'copy',
      pageTitle: 'Copy Project',
      code: 'PROJECT_COPY_TITLE',
    },
  ];

  const { t } = useTranslation();

  return (
    <StyledCreateProjectInfo>
      <div className="info_box">
        <div className="info_item">
          <div className="info_title">
            <CustomText fontSize={14} fontWeight={500}>
              <T>PROJECT_ID</T>
            </CustomText>
          </div>
          <div className="info_content">
            {useMemo(
              () => (
                <span
                  className="form__text"
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(caseId),
                  }}
                ></span>
              ),
              [caseId],
            )}
          </div>
        </div>
        {!isClinicUser ? (
          <div className="info_item">
            <div className="info_title">
              <CustomText fontSize={14} fontWeight={500}>
                <T>GLOBAL_CLIENT</T>
              </CustomText>
              <CustomSpan fontColor={color.red} marginLeft={5}>
                *
              </CustomSpan>
            </div>
            <div className="info_content">
              {pageType === 'edit' ? (
                <div className="projectInformation__edit_client_box">
                  <Controller
                    name="clientType"
                    control={informationControl}
                    render={({ onChange, value }) => (
                      <div className="projectInformation__edit_client_type">
                        {informationWatch('clientType') === 1 ? (
                          <T>GLOBAL_CLINIC</T>
                        ) : (
                          <T>GLOBAL_LABORATORY</T>
                        )}
                      </div>
                    )}
                  />
                  <Controller
                    name="client"
                    control={informationControl}
                    defaultValue=""
                    rules={{ required: true }}
                    render={({ onChange, value }) => (
                      <div className="projectInformation__edit_client_name">{clientName.value}</div>
                    )}
                  />
                </div>
              ) : (
                <div className="projectInformation__client_box">
                  <Controller
                    name="clientType"
                    control={informationControl}
                    render={({ onChange, value }) => (
                      <ButtonGroup className="projectInformation__client_btn_group">
                        <MuiButton
                          className={cx('projectInformation__client_btn md', {
                            active: value === 1,
                          })}
                          data-type="radio"
                          onClick={() => onChange(1)}
                        >
                          <T>GLOBAL_CLINIC</T>
                        </MuiButton>
                        <MuiButton
                          className={cx('projectInformation__client_btn md', {
                            active: value === 2,
                          })}
                          data-type="radio"
                          onClick={() => onChange(2)}
                        >
                          <T>GLOBAL_LABORATORY</T>
                        </MuiButton>
                      </ButtonGroup>
                    )}
                  />
                  <div className="projectInformation__client_input_item">
                    <Controller
                      name="client"
                      control={informationControl}
                      defaultValue=""
                      rules={{ required: true }}
                      render={({ onChange, value }) => (
                        <AsyncFetchSelect
                          className={cx('form__input_box client__select_box', {
                            isError:
                              informationErrors.client?.type === 'required' || isClientError.value,
                          })}
                          fullWidth
                          drondownHeight="200px"
                          inputProps={{
                            defaultValue: value,
                            selectedValue: value,
                            setSelectedValue: onChange,
                            setClientName: clientName.setValue,
                            data: projectPartners,
                            hasMoreData: hasMoreProjectPartners,
                            idKey: 'userGroupIdx',
                            labelKey: 'name',
                            onFetch: onFetchProjectPartners,
                            fetchType: 'fetch',
                            existNameValue: clientName.value,
                            isExternalSelected: isExternalSelected,
                          }}
                        />
                      )}
                    />
                    <WhiteButton borderRadius={4} onClick={onClickPartnerModal}>
                      <img src={icon_add} />
                    </WhiteButton>
                  </div>
                  {!isClinicUser &&
                  (informationErrors.client?.type === 'required' || isClientError.value) ? (
                    <div className="form__valid_text">
                      <T>NOT_FOUND_PARTNER</T>
                    </div>
                  ) : (
                    <CustomText fontColor={color.blue} fontSize={11} marginTop={5}>
                      <T>PROJECT_CLIENT_DETAIL</T>
                    </CustomText>
                  )}
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="info_item">
            <div className="info_title">
              <CustomText fontSize={14} fontWeight={500}>
                <T>GLOBAL_PATIENT</T>
              </CustomText>
              <CustomSpan fontColor={color.red}>*</CustomSpan>
            </div>
            <div className="info_content">
              {pageType === 'edit' ? (
                <span className="form__text">{patientName.value}</span>
              ) : (
                <div className="projectInformation__patient_input_item">
                  <Controller
                    name="patient"
                    control={informationControl}
                    defaultValue=""
                    rules={{ required: true }}
                    render={({ onChange, value }) => (
                      <AsyncFetchSelect
                        className="form__input_box patient__select_box"
                        disabled={pageType === 'edit'}
                        fullWidth
                        bgColor="transparent"
                        loadingIconColor="#868686"
                        dropIconColor="#868686"
                        drondownHeight="200px"
                        inputProps={{
                          defaultValue: patientCode?.value,
                          selectedValue: value,
                          setSelectedValue: onChange,
                          setClientName: patientName.setValue,
                          data: projectPatients,
                          hasMoreData: hasMoreProjectPatients,
                          initialValueName: patientName.value,
                          idKey: 'patientCodeIdx',
                          labelKey: 'listText',
                          onFetch: onFetchProjectPatients,
                          searchLoading: projectPatientsPending,
                          onSearch: onSearchProjectPatients,
                          existNameValue: patientName.value,
                          isExternalSelected: isExternalSelected,
                        }}
                      />
                    )}
                  />
                  <WhiteButton borderRadius={4} onClick={onClickPatientModal}>
                    <img src={icon_add} />
                  </WhiteButton>
                </div>
              )}
            </div>
          </div>
        )}
        <div className="info_item">
          <div className="info_title">
            <CustomText fontSize={14} fontWeight={500}>
              <T>PROJECT_DUE_DATE</T>
            </CustomText>
          </div>

          {isProjectCreator ? (
            <div className="info_content">
              <Controller
                name="dueDate"
                control={informationControl}
                rules={{ required: true }}
                render={({ onChange, value }) => (
                  <CustomDatePicker
                    fullWidth
                    borderColor="#c4c4c4"
                    height={40}
                    value={value}
                    onChange={onChange}
                    className={cx({
                      error: informationErrors.dueDate?.type === 'required',
                    })}
                  />
                )}
              />
              {informationErrors.dueDate?.type === 'required' && (
                <div className="form__valid_text">
                  <T>REGISTER_REQUIRED</T>
                </div>
              )}
            </div>
          ) : (
            <div className="info_content">
              <CustomText fontSize={14} fontWeight={500}>
                {!informationWatch('dueDate')
                  ? '-'
                  : language === 'en'
                  ? moment
                      .unix(informationWatch('dueDate'))
                      .locale(language)
                      .format('MMM. DD. YYYY HH:mm')
                  : moment
                      .unix(informationWatch('dueDate'))
                      .locale(language)
                      .format('YYYY. MM. DD  HH:mm')}
              </CustomText>
            </div>
          )}
        </div>

        <div className={cx('info_item', {})}>
          {!isProjectCreator ? (
            <>
              <div className="info_title"></div>
              <div className="info_content"></div>
            </>
          ) : (
            <>
              <div className="info_title">
                <CustomText fontSize={14} fontWeight={500}>
                  <T>PROJECT_MANAGER</T>
                </CustomText>
              </div>
              <div className="info_content">
                <Controller
                  name="manager"
                  control={informationControl}
                  defaultValue=""
                  render={({ onChange, value }) => (
                    <MuiWrapper className="form__input_box" isGlobalStyle>
                      <FormControl
                        fullWidth
                        variant="outlined"
                        error={informationErrors.client?.type === 'required'}
                      >
                        <Select
                          MenuProps={{
                            anchorOrigin: {
                              vertical: 'bottom',
                              horizontal: 'left',
                            },
                            getContentAnchorEl: null,
                            marginThreshold: 10,
                          }}
                          displayEmpty
                          value={value}
                          onChange={onChange}
                        >
                          <MenuItem value="" disabled>
                            <CustomSpan fontColor={'#00000065'}>
                              <T>PROJECT_MANAGER_PLACEHOLDER</T>
                            </CustomSpan>
                          </MenuItem>
                          {!!employeeList?.length &&
                            employeeList.map((item, idx) => (
                              <MenuItem key={idx} value={item.employeeIdx}>
                                {item.userName}
                              </MenuItem>
                            ))}
                        </Select>
                      </FormControl>
                    </MuiWrapper>
                  )}
                />
              </div>
            </>
          )}
        </div>
        <div className={cx('info_item', {})}>
          <div className="info_title">
            {!!isProjectCreator && (
              <CustomText fontSize={14} fontWeight={500}>
                <T>GLOBAL_REMAKE</T>
              </CustomText>
            )}
          </div>
          <div className="info_content">
            <div className={cx('remake_box', { syncedUser: !isProjectCreator })}>
              <div className="remake_none">
                {remake?.length === 0 && (
                  <CustomSpan fontSize={14} fontWeight={400}>
                    <T>GLOBAL_NONE</T>
                  </CustomSpan>
                )}
              </div>
              <div className="remake_add">
                <WhiteButton width={100} height={34} borderRadius={50} onClick={onClickRemakeModal}>
                  <CustomText fontSize={14} fontWeight={400}>
                    <T>GLOBAL_ADD</T>
                  </CustomText>
                </WhiteButton>
              </div>
            </div>

            <div className="remake_item">
              {useMemo(() => {
                if (!remakeTypeList) return;
                if (!isProjectCreator) return;
                return remake?.map((item: any, index: number) => {
                  const remakeTypeName = remakeTypeList?.find(
                    (type: any) => type?.remakeTypeIdx === item,
                  )?.remakeTypeName;

                  return (
                    <CustomSpan
                      fontSize={14}
                      fontWeight={400}
                      key={item}
                    >{`${item}. ${remakeTypeName}`}</CustomSpan>
                  );
                });
              }, [remake, remakeTypeList])}
            </div>
          </div>
        </div>
      </div>
      <div className={cx('projectInformation__design_box', { syncedUser: !isProjectCreator })}>
        <div className="projectInformation__design_title">
          <CustomText fontSize={14} fontWeight={500} fontColor="#444" marginBottom={8}>
            {`${t(`PROJECT_OPTION_DESIGN_SELECT`)}(${t(`GLOBAL_OPTION`)})`}
          </CustomText>
        </div>
        {/* Restoration Pontic Design */}
        <div className="projectInformation__design_option">
          <div className="design_option_title">
            <CustomText fontSize={13} fontWeight={400} fontColor="#444">
              <T>PROJECT_OPTION_RESTORATION_PONTIC</T>
            </CustomText>
          </div>
          <div className="design_icon_box">
            {restorationPonticIconList.map((item: any, index: number) => {
              const checked = item.id === Number(restorationPontic.value);
              // const checked = item.id === Number(restorationPontic.value);
              return (
                <label
                  className={cx('design_icon_radio_label pontic', {
                    active: checked,
                  })}
                  key={item.id}
                >
                  <img src={item.src} alt="tooth design icon" />
                  <input
                    type="checkbox"
                    name="restorationPontic"
                    className="tooth_design_input"
                    value={item.id}
                    checked={checked}
                    // onChange={restorationPontic.onChange}
                    onChange={() => {
                      if (restorationPontic.value === item.id) {
                        restorationPontic.onChange({ value: 0 });
                      } else {
                        restorationPontic.onChange({ value: Number(item.id) });
                      }
                    }}
                  />
                </label>
              );
            })}
          </div>
        </div>
        {/* Ceramic Metal Design */}
        <div className="projectInformation__design_option">
          <div className="design_option_title">
            <CustomText fontSize={13} fontWeight={400} fontColor="#444">
              <T>PROJECT_OPTION_CERAMIC_METAL</T>
            </CustomText>
          </div>
          <div className="design_icon_box">
            {ceramicMetalIconList.map((item: any, index: number) => {
              const checked = item.id === Number(ceramicMetal.value);
              return (
                <label
                  className={cx('design_icon_radio_label ceramic', {
                    active: checked,
                  })}
                  key={item.id}
                >
                  <img src={item.src} alt="tooth design icon" />
                  <input
                    type="checkbox"
                    name="ceramicMetal"
                    className="tooth_design_input"
                    value={item.id}
                    checked={checked}
                    // onChange={ceramicMetal.onChange}
                    onChange={() => {
                      if (ceramicMetal.value === item.id) {
                        ceramicMetal.onChange({ value: 0 });
                      } else {
                        ceramicMetal.onChange({ value: Number(item.id) });
                      }
                    }}
                  />
                </label>
              );
            })}
          </div>
        </div>
        {/* Occlusal Surface Design */}
        <div className="projectInformation__design_option">
          <div className="design_option_title">
            <CustomText fontSize={13} fontWeight={400} fontColor="#444">
              <T>PROJECT_OPTION_OCCLUSAL_SURFACE</T>
            </CustomText>
          </div>
          <div className="design_icon_box">
            {occlusalSurfaceIconList.map((item: any, index: number) => {
              const checked = item.id === Number(occlusalSurface.value);
              return (
                <label
                  className={cx('design_icon_radio_label occlusal', {
                    active: checked,
                  })}
                  key={item.id}
                >
                  <img src={item.src} alt="tooth design icon" />
                  <input
                    type="checkbox"
                    name="occlusalSurface"
                    className="tooth_design_input"
                    value={item.id}
                    checked={checked}
                    // onChange={occlusalSurface.onChange}
                    onChange={() => {
                      if (occlusalSurface.value === item.id) {
                        occlusalSurface.onChange({ value: 0 });
                      } else {
                        occlusalSurface.onChange({ value: Number(item.id) });
                      }
                    }}
                  />
                </label>
              );
            })}
          </div>
        </div>
      </div>
      <div className={cx('memo_box', {})}>
        <div className="memo_title">
          <CustomText fontSize={14} fontWeight={500}>
            <T>PROJECT_MEMO</T>
          </CustomText>
        </div>
        <Controller
          name="senderMemo"
          control={informationControl}
          render={({ onChange, value }) => (
            <MuiWrapper config={{ height: 80 }}>
              <TextField
                multiline
                rows={3}
                variant="outlined"
                fullWidth
                value={value}
                disabled={!isProjectCreator}
                onChange={e => {
                  if (e.target.value.length <= 300) {
                    // onChange(e);
                    onChange(validInputString(e.target.value));
                    // setMemoLength(e.target.value.length);
                  } else {
                    return;
                  }
                }}
              />
            </MuiWrapper>
          )}
        />
      </div>
    </StyledCreateProjectInfo>
  );
}

const StyledCreateProjectInfo = styled.section<{}>`
  /* background-color: blue; */
  display: flex;
  margin-right: 90px;
  width: 480px;
  /* flex: 1; */
  flex-direction: column;
  justify-content: space-between;

  .syncedUser {
    display: none !important;
  }

  .info_box {
    display: flex;
    flex-direction: column;
    row-gap: 5px;
    .info_item {
      display: flex;
      align-items: flex-start;
      .info_title {
        display: flex;
        width: 150px;
        height: 38px;
        align-items: center;
      }
      .info_content {
        display: flex;
        flex-direction: column;
        justify-content: center;
        width: 330px;
        /* height: 38px; */
        height: 100%;
        /* align-items: center; */
        .projectInformation__edit_client_box {
          display: flex;
          align-items: center;
          height: 40px;
          column-gap: 5px;
          .projectInformation__edit_client_type {
            width: 110px;
            height: 34px;
            display: flex;
            align-items: center;
            justify-content: center;
            border-radius: 5px;
            border: 1px solid #33b5e4;
            color: #33b5e4;
            font-size: 14px;
            font-weight: 500;
          }
          .projectInformation__edit_client_name {
            font-size: 14px;
            color: #444;
            display: flex;
            align-items: center;
          }
        }

        .projectInformation__patient_input_item {
          display: flex;
          column-gap: 5px;
        }
        .projectInformation__client_box {
          display: flex;
          flex-direction: column;
          .projectInformation__client_input_item {
            display: flex;
            column-gap: 5px;
          }
          .projectInformation__client_btn_group {
            width: 100%;
            margin-bottom: 5px;
            .projectInformation__client_btn {
              width: 50%;
            }
          }
        }
        .form__text {
          display: flex;
          align-items: center;
          height: 100%;
          font-size: 14px;
          font-weight: 400;
        }
        .remake__item_group {
          flex-wrap: wrap;
          row-gap: 10px;
          column-gap: 10px;
          display: none;
          margin-top: 10px;
          height: 0;
          transition: height 0.1s, transform 0.1s;
          &.visibled {
            display: flex;
            min-height: 100px;
            overflow: auto;
            transition: height 0.1s, transform 0.1s;
          }
          .remake__item_box {
            position: relative;
            width: 150px;
            height: 46px;
            display: block;
            border: 1px solid #dddee4;
            border-radius: 5px;
            display: flex;
            align-items: center;
            font-size: 12px;
            line-height: 21px;
            &:hover {
              cursor: pointer;
            }
            .remake__item_index {
              position: absolute;
              top: 12px;
              left: 12px;
              color: #dddee4;
            }
            .remake__item_text {
              width: 120px;
              max-height: 42px;
              margin: 0 24px 0 36px;
              overflow: hidden;
              text-overflow: ellipsis;
              word-break: break-all;
              white-space: break-spaces;
              display: flex;
            }
            &.checked {
              background-color: #f7fcfe;
              border: 1px solid #00a6e2;
              .remake__item_index {
                color: #00a6e2;
              }
              .remake__item_text {
                color: #00a6e2;
              }
            }
          }
        }
        .remake_box {
          display: flex;
          justify-content: space-between;
          .remake_none {
            display: flex;
            padding-top: 12px;
            flex-direction: column;
            row-gap: 10px;
          }
          .remake_add {
          }
        }
        .remake_item {
          display: flex;
          height: 100px;
          padding-top: 12px;
          flex-direction: column;
          row-gap: 10px;
          overflow: auto;
        }
      }
    }
  }
  .projectInformation__design_box {
    display: flex;
    flex-direction: column;
    row-gap: 14px;
    .projectInformation__design_title {
    }
    .projectInformation__design_option {
      display: flex;
      align-items: center;
      column-gap: 30px;
      .design_option_title {
        width: 120px;
      }
      .design_icon_box {
        display: inline-flex;
        flex-wrap: wrap;
        /* padding-left: 20px; */
        width: calc(100% - 200px);
        row-gap: 12px;
        column-gap: 12px;
        .design_icon_radio_label {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          position: relative;
          width: 35px;
          height: 35px;
          /* border: 1px solid #bbb; */
          border: 1px solid transparent;
          box-shadow: 0 0 3px rgba(0, 0, 0, 0.16);
          border-radius: 5px;
          text-align: center;
          cursor: pointer;
          user-select: none;
          /* margin-right: 12px; */
          /* &:not(:first-child) {
            margin-left: 12px;
          } */
          &.active {
            background-color: #e6e6e6;
            border-color: ${color.blue};
          }
          &.pontic {
            align-items: flex-end;
            img {
              height: 82%;
            }
          }
          &.ceramic img {
            height: 65%;
          }
          &.occlusal img {
            width: 75%;
          }
          .tooth_design_input {
            position: absolute;
            top: 0;
            left: 0;
            height: 0;
            width: 0;
            overflow: hidden;
          }
        }
      }
    }
  }
  .memo_box {
    display: flex;
    flex-direction: column;
    row-gap: 10px;
    .memo_title {
    }
  }
`;

export default CreateProjectInfo;
